import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
  Alert,
  Paper} from '@mui/material';
import {
  commitChanges,
  deleteRecord,
  insertRecord,
  handleLogout
} from './JSONUtils/Utility';
import './Checkalter.css';
import { TableViewer } from './JSONUtils/TableViewer';
import { Login } from './SSOLogin';
import { QueryInput } from './queryInput/QueryInput';

const INITIAL_OPTIONS = ["ck_metadata", "profile", "Enter custom query"];
const APPROVERS_LIST = [
  "kashish.bagga@salescode.ai", 
  "asees.singh@salescode.ai", 
  "mihir.bhasin@salescode.ai", 
  "rajeev@salescode.ai",
  "sandesh@salescode.ai",
  "snehadeep@salescode.ai"
];

// SSO configuration
const ssoConfig = {
  tenant: 'salescode_internal',
  appId: 'salescode_internal_app',
  apiBaseUrl: 'https://dev-auth.salescode.ai', 
  tokenEndpoint: '/v1/authenticate/sso/salescode_internal_google/token',
  userInfoEndpoint: '/v1/users/me'
};

export function Checkalter() {
  const [showViewer, setShowViewer] = useState(false);
  const [options] = useState<string[]>(INITIAL_OPTIONS);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [approvers] = useState<string[]>(APPROVERS_LIST);
  const [selectedOptionApprover, setSelectedOptionApprover] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');

  useEffect(() => {
    const storedEmail = localStorage.getItem('username') || localStorage.getItem('email');
    if (storedEmail) {
      setUserEmail(storedEmail);
      setIsLoggedIn(true);
    }
  }, []);  

  const handleLoginSuccess = (data: { emailId: string; token: string }) => {
    localStorage.setItem('email', data.emailId);
    setIsLoggedIn(true);
    setUserEmail(data.emailId);
    setError(null);
  };

  const handleLoginFailure = (errorMessage: string) => {
    setError(errorMessage || 'Authentication failed');
  };

  const handleLogoutClick = () => {
    handleLogout();
    setIsLoggedIn(false);
    setUserEmail('');
    setShowViewer(false);
    setSelectedOption('');
    setSelectedOptionApprover('');
  };

  const handleOptionSelect = (event: SelectChangeEvent) => {
    const option = event.target.value;
    if (!option) return;
    setSelectedOption(option);
    setShowViewer(true);
  };

  const handleApproverSelect = (event: SelectChangeEvent) => {
    const option = event.target.value;
    if (!option) return;
    setSelectedOptionApprover(option);
  };

  if (!isLoggedIn) {
    return (
      <Login 
        config={ssoConfig}
        onLoginSuccess={handleLoginSuccess}
        onLoginFailure={handleLoginFailure}
      />
    );
  }

  return (
    <Box sx={{
      p: 3,
      margin: '0 auto'
    }} className="container">
      <Paper elevation={3} sx={{
        p: 3,
        mb: 3,
        width: '100%'
      }} className="header-container">
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10, 
          flexWrap: 'wrap'
        }}>
          <Typography 
            variant="subtitle1" 
            component="div" 
            sx={{ 
              width: '500px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 0
            }}
          >
            Welcome, {userEmail}!
          </Typography>
          
          <Box sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexGrow: 1,
            justifyContent: 'flex-end',
            minWidth: 'fit-content'
          }}>
            <FormControl sx={{ minWidth: '280px', maxWidth: '280px' }} size="small">
              <InputLabel id="table-select-label">Select table</InputLabel>
              <Select
                labelId="table-select-label"
                id="table-select"
                value={selectedOption}
                label="Select table"
                onChange={handleOptionSelect}
              >
                {options.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: '280px', maxWidth: '280px' }} size="small">
              <InputLabel id="approver-select-label">Select approver</InputLabel>
              <Select
                labelId="approver-select-label"
                id="approver-select"
                value={selectedOptionApprover}
                label="Select approver"
                onChange={handleApproverSelect}
              >
                {approvers.map((approver) => (
                  <MenuItem key={approver} value={approver}>
                    {approver}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              sx={{
                backgroundColor: '#C44C36',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#A53C2C',
                },
                minWidth: '120px',
                height: '40px',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '14px',
                flexShrink: 0
              }}
              onClick={handleLogoutClick}
            >
              Logout
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      {showViewer && selectedOption && (
        selectedOption === "ck_metadata" || selectedOption === "profile" ? (
          <TableViewer
            selectedOption={selectedOption}
            onExecuteChanges={commitChanges}
            onDeleteRecord={deleteRecord}
            onInsertRecord={insertRecord}
            userEmail={userEmail}
            selectedOptionApprover={selectedOptionApprover}
          />
        ) : (
          <>
          <TableViewer
            selectedOption={selectedOption}
            onExecuteChanges={commitChanges}
            onDeleteRecord={deleteRecord}
            onInsertRecord={insertRecord}
            userEmail={userEmail}
            selectedOptionApprover={selectedOptionApprover}
            isCustomQuery={true}
          />
          <QueryInput
            selectedOption={selectedOption}
            userEmail={userEmail}
            selectedOptionApprover={selectedOptionApprover}
          />
          </>
          
        )
      )}
    </Box>
  );
}